/**
 * What a reading link's thread item says once opened (decision 58; part 38d log, DP6, LW10, LW11): a reader's comment on
 * a passage of a shared chapter (its range, quote and context in the copy's visible text, part 23c's anchor), or a reply
 * naming the item it answers, by the reader or the writer; each with its words as plain text (decision 59) and the name
 * the reader gave. UTF-8 JSON. `readItem` takes what a reader, or the relay, wrote: anything but exactly an item within
 * its bounds is null, never stored. Pure.
 */
import { CONTEXT, MAX_QUOTE } from '../comments/anchor.ts';

/** The version written. */
export const ITEM_VERSION = 1;

/** The most UTF-16 units an item's words hold (the relay's 16 KiB an item bounds the rest). */
export const MAX_ITEM_TEXT = 10_000;

/** The most code points a reader's name keeps. */
export const MAX_READER_NAME = 80;

/** The most UTF-16 units of context an item may give each side (the comment's anchor keeps {@link CONTEXT}). */
export const MAX_ITEM_CONTEXT = 4 * CONTEXT;

/** A comment's place in the copy's chapter (part 23c, AN1): its range (null: not given), quote and context. */
export interface ItemAnchor {
  readonly range: { readonly from: number; readonly to: number } | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
}

/** An item opened. */
export type ReadingItem =
  | {
      readonly kind: 'comment';
      /** The shared chapter it is on (32 lowercase hex digits). */
      readonly chapter: string;
      /** Its passage; null for the whole chapter. */
      readonly anchor: ItemAnchor | null;
      readonly text: string;
      readonly name: string;
    }
  | {
      readonly kind: 'reply';
      /** The item it answers, its ID as 32 lowercase hex digits. */
      readonly to: string;
      readonly text: string;
      readonly name: string;
    };

/** An ID: 32 lowercase hex digits. */
export const ID = /^[0-9a-f]{32}$/u;
/** Strict UTF-8. */
export const UTF8 = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
/** A lone surrogate (with the `u` flag a pair is one code point): text no store or page takes as it is, as a NUL isn't. */
const LONE_SURROGATE = /\p{Cs}/u;

/**
 * A reader's name as kept: every run of white space and control characters one space, trimmed, cut to
 * {@link MAX_READER_NAME} code points (never half a pair), trimmed again; empty when nothing readable is left.
 */
export function cleanReaderName(name: string): string {
  const one = name.replaceAll(/[\s\p{Cc}]+/gu, ' ').trim();
  return Array.from(one).slice(0, MAX_READER_NAME).join('').trim();
}

/** `item` as its bytes. */
export function encodeItem(item: ReadingItem): Uint8Array<ArrayBuffer> {
  const body =
    item.kind === 'comment'
      ? { v: ITEM_VERSION, kind: item.kind, chapter: item.chapter, anchor: item.anchor === null ? null : anchorJson(item.anchor), text: item.text, name: item.name }
      : { v: ITEM_VERSION, kind: item.kind, to: item.to, text: item.text, name: item.name };
  return new TextEncoder().encode(JSON.stringify(body));
}

/** `anchor` as its JSON fields. */
export function anchorJson(anchor: ItemAnchor): Record<string, unknown> {
  return { range: anchor.range === null ? null : { from: anchor.range.from, to: anchor.range.to }, quote: anchor.quote, before: anchor.before, after: anchor.after };
}

/** The own fields of `value` when it is a plain JSON object of exactly `keys`; null otherwise (part 38e's note items too). */
export function exactly(value: unknown, keys: readonly string[]): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === keys.length && keys.every((key) => fields.has(key)) ? fields : null;
}

/** `value` when it is text of at most `max` UTF-16 units fit to keep. */
export function fit(value: unknown, max: number): string | null {
  return typeof value === 'string' && value.length <= max && !LONE_SURROGATE.test(value) && !value.includes('\u0000') ? value : null;
}

/** A whole number from 0 below 2^31. */
function offset(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value < 2 ** 31 ? value + 0 : null;
}

/** An anchor's fields, or null. */
export function anchorOf(value: unknown): ItemAnchor | null {
  const fields = exactly(value, ['range', 'quote', 'before', 'after']);
  if (fields === null) return null;
  const quote = fit(fields.get('quote'), MAX_QUOTE);
  const before = fit(fields.get('before'), MAX_ITEM_CONTEXT);
  const after = fit(fields.get('after'), MAX_ITEM_CONTEXT);
  if (quote === null || before === null || after === null) return null;
  const raw = fields.get('range');
  if (raw === null) return { range: null, quote, before, after };
  const range = exactly(raw, ['from', 'to']);
  const from = offset(range?.get('from'));
  const to = offset(range?.get('to'));
  return from === null || to === null || to < from ? null : { range: { from, to }, quote, before, after };
}

/**
 * DP6, LW10: the item `bytes` hold, or null: bytes that aren't strict UTF-8 JSON, another version, a kind but a comment
 * or a reply, a field more or less, an ID of the wrong form, words empty, over {@link MAX_ITEM_TEXT} or holding a NUL or
 * a lone surrogate, an anchor out of its bounds. The name is kept cleaned ({@link cleanReaderName}).
 */
export function readItem(bytes: Uint8Array): ReadingItem | null {
  let value: unknown;
  try {
    value = JSON.parse(UTF8.decode(bytes));
  } catch {
    return null;
  }
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  const fields = exactly(value, kind === 'comment' ? ['v', 'kind', 'chapter', 'anchor', 'text', 'name'] : ['v', 'kind', 'to', 'text', 'name']);
  if (fields?.get('v') !== ITEM_VERSION) return null;
  const text = fit(fields.get('text'), MAX_ITEM_TEXT);
  const name = fit(fields.get('name'), 4 * MAX_READER_NAME);
  if (text === null || text.trim() === '' || name === null) return null;
  if (kind === 'comment') {
    const chapter = fields.get('chapter');
    const raw = fields.get('anchor');
    const anchor = raw === null ? null : anchorOf(raw);
    if (typeof chapter !== 'string' || !ID.test(chapter) || (raw !== null && anchor === null)) return null;
    return { kind, chapter, anchor, text, name: cleanReaderName(name) };
  }
  const to = fields.get('to');
  if (kind !== 'reply' || typeof to !== 'string' || !ID.test(to)) return null;
  return { kind, to, text, name: cleanReaderName(name) };
}
